import { motion, useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowRight, Mail, Building2, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";
import { API_URL } from '../../config/api';
const API = `${API_URL}/api`;
const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/d77c0364065b8ec483b9e6915ff1d30475de2851aefe5560f16a14318cc351be.png";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function FinalCTASection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      await axios.post(`${API}/leads`, { email, source: 'final-cta' });
      toast.success('Merci ! Nous te contacterons bientot.');
      setEmail('');
    } catch (error) {
      toast.error('Une erreur est survenue. Veuillez reessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      ref={ref}
      className="section-premium bg-ba-primary relative overflow-hidden"
      data-testid="final-cta-section"
    >
      {/* Animated background decorations */}
      <div className="absolute inset-0">
        <motion.div 
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-ba-secondary/10 rounded-full blur-3xl" 
        />
        <motion.div 
          animate={{ 
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-full blur-3xl" 
        />
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-4xl relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center"
        >
          {/* Logo with scale animation - AGRANDI */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center mb-8"
          >
            <motion.img 
              src={LOGO_URL} 
              alt="BatiAssist" 
              className="h-16 sm:h-20 md:h-24 brightness-0 invert drop-shadow-lg"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 400 }}
            />
          </motion.div>

          {/* Title with word-by-word reveal effect */}
          <motion.h2 
            variants={itemVariants}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight"
          >
            Et si ton entreprise fonctionnait{' '}
            <motion.span 
              className="text-ba-secondary inline-block"
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(249, 115, 22, 0)",
                  "0 0 40px rgba(249, 115, 22, 0.3)",
                  "0 0 20px rgba(249, 115, 22, 0)",
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              enfin differemment ?
            </motion.span>
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="text-lg md:text-xl text-white/70 mb-10 md:mb-12 max-w-2xl mx-auto"
          >
            Réserve un entretien avec Romain pour decouvrir comment BatiAssist 
            peut transformer ton organisation.
          </motion.p>

          {/* Primary CTA with enhanced glow */}
          <motion.div
            variants={itemVariants}
            className="mb-10"
          >
            <motion.a 
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button 
                className="bg-ba-secondary hover:bg-ba-secondary/90 text-white font-bold rounded-full px-10 sm:px-12 py-7 sm:py-8 text-lg sm:text-xl shadow-2xl btn-glow-pulse group"
                data-testid="final-cta-button"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Reserver un entretien avec Romain
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform duration-300" />
              </Button>
            </motion.a>
          </motion.div>

          {/* Divider with line animation */}
          <motion.div 
            variants={itemVariants}
            className="flex items-center gap-4 max-w-md mx-auto mb-10"
          >
            <motion.div 
              initial={{ width: 0 }}
              animate={isInView ? { width: "100%" } : {}}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex-1 h-px bg-white/20" 
            />
            <span className="text-white/50 text-sm font-medium">ou laisse-nous tes coordonnées</span>
            <motion.div 
              initial={{ width: 0 }}
              animate={isInView ? { width: "100%" } : {}}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="flex-1 h-px bg-white/20" 
            />
          </motion.div>

          {/* Email capture with slide up */}
          <motion.form
            variants={itemVariants}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
          >
            <motion.div 
              className="flex-1 relative"
              whileFocus={{ scale: 1.02 }}
            >
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="email"
                placeholder="Ton email professionnel"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 py-6 rounded-full bg-white border-transparent text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-ba-secondary"
                data-testid="final-cta-email"
              />
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button 
                type="submit"
                disabled={isLoading}
                className="bg-white text-ba-primary hover:bg-white/90 font-semibold rounded-full px-8 py-6 transition-all hover:shadow-lg"
                data-testid="final-cta-submit"
              >
                {isLoading ? (
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    ⟳
                  </motion.span>
                ) : 'Etre recontacte'}
              </Button>
            </motion.div>
          </motion.form>

          <motion.p
            variants={itemVariants}
            className="text-white/50 text-sm mt-8"
          >
            Diagnostic gratuit • Sans engagement • Reponse sous 24h
          </motion.p>
        </motion.div>
      </div>

      {/* Footer with fade in */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ delay: 1, duration: 0.5 }}
        className="container mx-auto px-4 md:px-6 max-w-7xl mt-20 pt-8 border-t border-white/10"
      >
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-white/50 text-sm">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            <span>BatiAssist - L'assistant digital des artisans du batiment</span>
          </div>
          <div className="flex gap-6">
            <span>© 2024 BatiAssist</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
